import React, { useEffect, useState, useRef } from 'react';
import DevelopmentCard from './DevelopmentCard';
import PropertyCard from './PropertyCard';
import { Reorder, AnimatePresence } from 'framer-motion';
import PropertyMap from './PropertyMap';
import Pagination from './Pagination';
import ViewControls from './ViewControls';

function SearchListing({
  resultsMode,
  results,
  property,
  searchMode,
  viewMode,
  viewModes,
  onViewModeChange,
  filters,
  onPageChange,
  clearFilters,
  isSearching,
}) {
  const [searchSummary, setSearchSummary] = useState('');
  const prevSearchModeRef = useRef(searchMode);

  useEffect(() => {
    if (
      window.globalOverlays &&
      typeof window.globalOverlays.reinitDynamicContent === 'function'
    ) {
      window.globalOverlays.reinitDynamicContent();
    }
  }, [results]);

  useEffect(() => {
    if (!isSearching && results.posts && resultsMode === searchMode) {
      let summary = '';
      let propertyType = searchMode;
      let radius = filters.radius;

      if (searchMode === 'hotw') {
        propertyType = 'homes of the week';
      }

      if (radius === '9999') {
        radius = '30+ miles';
      }

      summary += `${results.found_posts} ${propertyType.replace(/-/g, ' ')} `;

      if (filters.address && radius) {
        summary += ` found within ${radius} miles of ${filters.address}`;
      }

      setSearchSummary(summary);
      prevSearchModeRef.current = searchMode;
    }
  }, [results, searchMode, isSearching, resultsMode]);

  return (
    <>
      <div className="search-listing mb-8 gap-4 flex items-center justify-between">
        <div className="text-sm font-medium text-blue-700">{searchSummary}</div>

        <ViewControls
          searchMode={searchMode}
          viewMode={viewMode}
          viewModes={viewModes}
          onViewModeChange={onViewModeChange}
        />
      </div>

      <div
        className={`list-container ${viewMode === 'grid' ? 'block' : 'hidden'}`}
      >
        {results.posts && (
          <Reorder.Group
            axis="y"
            values={results.posts}
            onReorder={() => {}}
            className="grid md:grid-cols-12 gap-6 lg:gap-8"
          >
            <AnimatePresence mode="popLayout">
              {results.posts.map((item, i) => (
                <Reorder.Item
                  key={`${item?.id}`}
                  value={item}
                  className={`list-none  ${
                    resultsMode !== 'developments'
                      ? 'md:col-span-6 xl:col-span-4'
                      : 'col-span-6'
                  }`}
                  dragListener={false}
                  layout="position"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{
                    duration: 0.3,
                    ease: 'easeInOut',
                    delay: i * 0.05,
                    layout: {
                      duration: 0.4,
                      ease: 'easeInOut',
                    },
                  }}
                >
                  {resultsMode !== 'developments' ? (
                    <PropertyCard key={item.id} {...item} />
                  ) : (
                    <DevelopmentCard key={item.id} {...item} />
                  )}
                </Reorder.Item>
              ))}
            </AnimatePresence>
          </Reorder.Group>
        )}

        {results.posts && results.posts.length === 0 && (
          <div className="text-xl bg-white mb-3 text-blue-700 rounded p-7">
            <h2 className="mb-4">
              No properties found matching your criteria.
            </h2>

            <button
              onClick={clearFilters}
              className="bg-blue-700 text-sm flex items-center gap-1 hover:bg-pink-500 hover:text-black text-white px-4 py-3 rounded font-medium transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="15"
                viewBox="0 0 15 15"
                fill="none"
                aria-hidden="true"
                focusable="false"
              >
                <rect
                  x="2.82812"
                  y="4.24264"
                  width="2"
                  height="10"
                  transform="rotate(-45 2.82812 4.24264)"
                  fill="currentColor"
                />
                <rect
                  x="2.82812"
                  y="9.8995"
                  width="10"
                  height="2"
                  transform="rotate(-45 2.82812 9.8995)"
                  fill="currentColor"
                />
              </svg>
              Clear filters
            </button>
          </div>
        )}
      </div>
      <div
        className={`map-container ${viewMode === 'map' ? 'block' : 'hidden'}`}
      >
        <PropertyMap
          listings={results.posts}
          clearFilters={clearFilters}
          viewMode={viewMode}
          searchMode={searchMode}
        />
      </div>

      {results.max_num_pages > 1 && (
        <Pagination
          currentPage={filters?.page || 1}
          totalPages={results.max_num_pages}
          onPageChange={onPageChange}
        />
      )}
    </>
  );
}

export default SearchListing;
